import { encounterCreatureField, encounterField } from '../../shared/array-types';
import { readInventoryField, readInventoryRows } from '../compile/inventory-raw-readers';
import { readItemField } from '../compile/item-field-reader';
import { statusRows } from '../statuses/expiry';
import type { AppliedFrame, AppliedRow } from './applied-frame';

const upper = (value: unknown): string => String(value ?? '').toUpperCase();
const record = (value: unknown): value is AppliedRow => value !== null && typeof value === 'object' && !Array.isArray(value);
const lists = ['encounter_creatures_arr', 'inventory', 'inventory_in_container', 'encounter_inventories_arr'] as const;
interface Index { slots: Map<string, Set<number>>; keys: Set<string>[] }
const indexes = new WeakMap<unknown[], Index>();
const edits = new WeakMap<unknown[], { previous: number; slots: Set<number> }>();
const listIds = new WeakMap<unknown[], number>();
let nextListId = 0;
const listId = (list: unknown[]): number => { let id = listIds.get(list); if (id === undefined) { id = ++nextListId; listIds.set(list, id); } return id; };
const counters = { visits: 0, paintVisits: 0, changes: 0, noops: 0, indexedRows: 0, rowCopies: 0, ancestorCopies: 0, listCopies: 0 };
export const appliedTreeMetrics = () => ({ ...counters });
export const countAppliedCopy = (kind: 'rowCopies' | 'ancestorCopies' | 'listCopies') => { counters[kind]++; };
export const countAppliedVisit = () => { counters.visits++; };
export const countAppliedPaintVisit = () => { counters.paintVisits++; };
export const countAppliedResult = (changed: boolean) => { if (changed) counters.changes++; else counters.noops++; };

export function appliedFrameRoutes(frame: AppliedFrame): Set<string> {
    const keys = new Set<string>();
    const add = (table: string, id: unknown) => { if (id) keys.add(`${table}:${upper(id)}`); };
    for (const id of new Set([...Object.keys(frame.member_revs), ...Object.keys(frame.computed), ...Object.keys(frame.computed_delta ?? {})])) {
        add('encounter_creatures', id);
        if (Object.prototype.hasOwnProperty.call(frame.computed_delta ?? {}, id)) add('inventory', id);
    }
    for (const row of [...frame.rows, ...frame.deletes]) add(row.table, row.row_id);
    for (const row of frame.deletes) if (row.table === 'encounter_creatures') add('summons', row.row_id);
    for (const { table, row } of frame.rows) {
        if (table === 'statuses') for (const key of ['status_target_id', 'status_caster_id']) {
            const id = readItemField(row, key); add('owner', id); add('encounter_creatures', id);
        }
        if (table === 'charges') { const id = readItemField(row, 'charge_holder'); add('owner', id); add('encounter_creatures', id); }
        if (table === 'encounter_creature_triggers') add('encounter_creatures', readItemField(row, 'caster_encounter_creature_id'));
        if (table === 'encounter_links') add('owner', readItemField(row, 'encounter_link_from'));
        if (table === 'inventory') add('owner', readInventoryField({ inventory: row }, 'possess_id'));
    }
    for (const birth of frame.new_entities) {
        add('encounter_creatures', birth.entity_id);
        add('encounter_creatures', encounterCreatureField(birth.member.encounter_creatures, 'controller_encounter_creature_id'));
    }
    for (const birth of frame.new_inventory ?? []) add('owner', birth.possess_id);
    if (record(frame.inputs?.catalog) && Object.keys(frame.inputs.catalog).length) keys.add('catalog');
    if (record(frame.inputs?.member_catalogs)) for (const id of Object.keys(frame.inputs.member_catalogs)) add('encounter_creatures', id);
    return keys;
}
function rowKeys(value: unknown, refresh = false): Set<string> {
    const keys = new Set<string>();
    if (!record(value)) return keys;
    counters.indexedRows++;
    const add = (table: string, id: unknown) => { if (id) keys.add(`${table}:${upper(id)}`); };
    const creature = encounterCreatureField(value.encounter_creatures, 'encounter_creature_id');
    const inventory = readInventoryField(value, 'inventory_id');
    add('encounter_creatures', creature); add('inventory', inventory); add('encounters', encounterField(value, 'encounter_id'));
    if (record(value.player_characters)) add('player_characters', readItemField(value.player_characters, 'item_id'));
    add('owner', creature || inventory || value.entity_id || encounterField(value, 'encounter_id'));
    if (record(value.catalog)) keys.add('catalog');
    // A summon tombstone can omit its controller. Its held SELECT/display list
    // is still a consumer of that child's identity and must be pruned.
    const ec = record(value.encounter_creatures) ? value.encounter_creatures : {};
    const summons = Array.isArray(value.caster_summons) ? value.caster_summons : Array.isArray(ec.caster_summons) ? ec.caster_summons : [];
    for (const row of summons) if (record(row)) add('summons', readItemField(row, 'encounter_creature_id'));
    for (const side of ['target', 'caster'] as const) for (const row of statusRows(value, side)) add('statuses', readItemField(row, 'status_id'));
    for (const row of Array.isArray(value.charges) ? value.charges : []) if (record(row)) add('charges', readItemField(row, 'charge_id'));
    for (const row of Array.isArray(value.counterspell_watches) ? value.counterspell_watches : []) if (record(row)) add('encounter_creature_triggers', readItemField(row, 'trigger_id'));
    for (const key of ['encounter_links', 'inventory_encounter_link'])
        for (const row of Array.isArray(value[key]) ? value[key] : []) if (record(row)) add('encounter_links', readItemField(row, 'encounter_link_id'));
    for (const key of lists) {
        const child = value[key];
        if (key === 'inventory' && record(child) && inventory) continue;
        const list = Array.isArray(child) ? child : record(child) && key !== 'encounter_creatures_arr' ? readInventoryRows(value, key) : undefined;
        if (list) for (const token of indexList(list, refresh).slots.keys()) keys.add(token);
    }
    return keys;
}
function indexList(list: unknown[], refresh = false): Index {
    const held = indexes.get(list); if (!refresh && held && held.keys.length === list.length) return held;
    const index: Index = { slots: new Map(), keys: [] }; indexes.set(list, index);
    for (let slot = 0; slot < list.length; slot++) update(index, slot, rowKeys(list[slot], refresh));
    return index;
}
function update(index: Index, slot: number, keys: Set<string>): void {
    for (const key of index.keys[slot] ?? []) { const slots = index.slots.get(key)!; slots.delete(slot); if (!slots.size) index.slots.delete(key); }
    index.keys[slot] = keys;
    for (const key of keys) { const slots = index.slots.get(key) ?? new Set<number>(); slots.add(slot); index.slots.set(key, slots); }
}
export function appliedListSlots(list: unknown[], wanted: ReadonlySet<string>, _creaturesOnly = false, deleted?: ReadonlySet<string>): Set<number> {
    let index = indexList(list);
    // An optimistic writer can add a status/charge without replacing its list or
    // receiving an echo. A missing delete route must check the current rows.
    if (deleted && [...deleted].some(key => !index.slots.has(key))) index = indexList(list, true);
    const slots = new Set<number>();
    for (const key of wanted) for (const slot of index.slots.get(key) ?? []) slots.add(slot);
    return slots;
}
export function retainAppliedListIndex(previous: unknown[], next: unknown[], slots: ReadonlySet<number>, identitiesChanged: boolean): void {
    const prior = indexes.get(previous); if (!prior) return;
    if (previous.length !== next.length) { indexes.delete(next); return; }
    const index = previous === next ? prior : { slots: new Map([...prior.slots].map(([key, slots]) => [key, new Set(slots)])), keys: prior.keys.slice() };
    if (identitiesChanged) for (const slot of slots) update(index, slot, rowKeys(next[slot]));
    indexes.set(next, index);
    if (previous !== next) edits.set(next, { previous: listId(previous), slots: new Set(slots) });
}
export function appliedListEdits(previous: unknown, next: unknown[]): ReadonlySet<number> | undefined {
    const held = edits.get(next); return held && Array.isArray(previous) && held.previous === listIds.get(previous) ? held.slots : undefined;
}
